import React from 'react'

import { Box, Button, Text, ToggleButton } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import TimePicker from 'modules/core/components/TimePicker'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { useCustomTimeRange } from 'modules/attendance/hooks/useCustomTimeRange'
import { useFetchCustomTimeRangeCategories } from 'modules/attendance/hooks/useFetchCustomTimeRangeCategories'
import {
  hasEndTime,
  hasStartTime,
} from 'modules/attendance/lib/forms/CreateCustomTimeRangeFormTimeSettings'
import { CustomTimeRangesFormData } from 'modules/attendance/lib/forms/utils/CustomTimeRangesFormData'
import { translateDayInSingular } from 'modules/attendance/lib/translateDays'

import CustomCategorySelector from './CategorySelector'
import { Step } from './utils/stepTypes'

interface NewTimeBlockModalProps {
  onClose: () => void
  form: FormikProps<CustomTimeRangesFormData>
  onSubmitNextButton: () => void
  onSubmitWizard: () => void
  openCustomOptions: boolean
  setOpenCustomOptions: (value: boolean) => void
  isEdit: boolean
  settingId: number
  step: Step
  totalSteps: number
}

const NewTimeBlockModal: React.FC<NewTimeBlockModalProps> = ({
  onClose,
  form,
  onSubmitNextButton,
  onSubmitWizard,
  openCustomOptions,
  setOpenCustomOptions,
  isEdit,
  settingId,
  step,
  totalSteps,
}) => {
  const { dayEnums } = useCustomTimeRange()
  const { categories: customTimeRangeCategories } = useFetchCustomTimeRangeCategories(settingId)

  const handleOnChangeDay = (value: unknown) => {
    return form.setFieldValue('day', value)
  }

  const resetTimeFields = () => {
    form.setFieldValue('start_time', undefined)
    form.setFieldValue('end_time', undefined)
  }

  const removeCustomOptions = () => {
    setOpenCustomOptions(false)
    if (!isEdit) resetTimeFields()
  }

  const selectedCategory = customTimeRangeCategories.customTimeRangeCategories.find(
    (category) => category.id === Number(form.values.attendance_custom_time_range_category_id)
  )
  const isNewBlockRegular = selectedCategory?.isRegular

  const onSubmitNextButtonWrapper = () => {
    if (isEdit && !openCustomOptions) resetTimeFields()

    if (isNewBlockRegular) {
      onSubmitWizard()
    } else {
      onSubmitNextButton()
    }
  }

  const footerButton = (
    <>
      <Button type="secondary" onClick={onClose}>
        {i18n.t('common.cancel')}
      </Button>
      <Button onClick={onSubmitNextButtonWrapper}>
        {isNewBlockRegular ? i18n.t('common.save') : i18n.t('common.continue')}
      </Button>
    </>
  )

  const options = dayEnums.map((day) => {
    const translatedDay = translateDayInSingular(day)

    return (
      <Selector.Option
        key={translatedDay}
        value={day}
        label={translatedDay}
        searchValue={translatedDay}
      >
        {translatedDay}
      </Selector.Option>
    )
  })

  const title = isEdit
    ? i18n.t('time_tracking.settings.time_blocks.categories.modals.edit_block')
    : i18n.t('time_tracking.settings.time_blocks.categories.modals.new_block')

  const progress = isNewBlockRegular ? undefined : { amount: step, total: totalSteps }

  useEffect(() => {
    if (selectedCategory) {
      form.setValues({
        ...form.values,
        time_settings_custom_time_range_category_id:
          selectedCategory.timeSettingsCustomTimeRangeCategoryId.toString(),
      })
    }
    if (isNewBlockRegular) {
      form.setFieldValue('pool_type', 'balance')
    }
  }, [selectedCategory?.timeSettingsCustomTimeRangeCategoryId])

  return (
    <Modal
      illustration="night"
      title={title}
      onClose={onClose}
      footer={footerButton}
      progress={progress}
    >
      <Box flexDirection="column" gap="s8">
        <Box gap="s16">
          <CustomCategorySelector
            form={form}
            settingId={settingId}
            selectedCategory={selectedCategory}
          />
          <Box width="full">
            <Selector.Popover
              label={i18n.t('time_tracking.settings.time_blocks.categories.modals.days')}
              placeholder={i18n.t('common.select')}
              blankLabel={i18n.t('common.select')}
              value={form.values.day}
              onChange={handleOnChangeDay}
              isMultiSelect
              headerLabel={i18n.t('time_tracking.settings.time_blocks.categories.modals.days')}
              error={form.errors.day?.toString()}
              isDisabled={isEdit}
              hasSearch={false}
            >
              {options}
            </Selector.Popover>
          </Box>
          <Box gap="s8">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('time_tracking.settings.time_blocks.categories.modals.apply_to')}
            </Text.Normal>
            <Box flexDirection="row" gap="s16">
              <ToggleButton
                toggled={!openCustomOptions}
                onClick={removeCustomOptions}
                label={i18n.t('time_tracking.settings.time_blocks.categories.modals.full_day')}
              />
              <ToggleButton
                toggled={openCustomOptions}
                onClick={() => setOpenCustomOptions(true)}
                label={i18n.t(
                  'time_tracking.settings.time_blocks.categories.modals.specific_time_slot'
                )}
              />
            </Box>
          </Box>
          {openCustomOptions && (
            <Box flexDirection="row" gap="s16">
              <TimePicker
                label={i18n.t('time_tracking.settings.custom_time_range_section.modals.start_time')}
                onChange={(value: string) => form.setFieldValue('start_time', value)}
                value={hasStartTime(form) ? form.values.start_time : undefined}
                mandatory
                error={form.errors.start_time}
              />
              <TimePicker
                label={i18n.t('time_tracking.settings.custom_time_range_section.modals.end_time')}
                onChange={(value: string) => form.setFieldValue('end_time', value)}
                value={hasEndTime(form) ? form.values.end_time : undefined}
                mandatory
                error={form.errors.end_time}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export default NewTimeBlockModal
